import { Rect } from '../lib/region';
import { renderNode } from './node';
import { drawBackground } from './utils/background';
import { prepareLayerCache } from './utils/layers';
import { layoutNode } from './utils/layout';
import { renderGeneratedBy } from './utils/logo';
import './utils/polyfill';

export { renderNode };

export function paint(renderContext) {
  const context = renderContext.context;
  const dirtyProvider = renderContext.providers?.dirty;

  if (renderContext.shouldSaveRender && renderContext.shouldSaveRender()) {
    if (renderContext.renderIndex === renderContext.lastRenderIndex) return;
    renderContext.lastRenderIndex = renderContext.renderIndex || 0;
  }

  const canvas = context.canvas;
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;
  context.clearRect(0, 0, canvasWidth, canvasHeight);
  if (!renderContext.card) return;

  context.save();
  const scaleProvider = renderContext.providers.scale;
  const scale = scaleProvider.scale;

  context.scale(scale, scale);
  context.translate(scaleProvider.translateX / scale, scaleProvider.translateY / scale);

  render(renderContext);

  context.restore();

  // 标记渲染完成
  if (dirtyProvider) {
    dirtyProvider.markRenderComplete();
  }

  if (renderContext.providers?.interactive?.render && renderContext.mode !== 'presentation') {
    renderContext.providers.interactive?.render(renderContext);
  }
}

export function render(renderContext) {
  if (!renderContext.card) return;
  switch (renderContext.card.type) {
    case 'markdown':
      return renderMarkdownCard(renderContext);
    case 'openGraph':
    case 'seedeGraphic':
    default:
      return renderSeedeGraph(renderContext);
  }
}

function isNodeInViewport(renderContext, node) {
  const viewport = renderContext.viewport;
  const nodeRect = renderContext.api.getNodeRegion(node).rect;
  return viewport.intersection(nodeRect);
}

function renderSeedeGraph(renderContext) {
  const nodes = renderContext.api.getNodes();
  const nodeCount = nodes.length;

  prepareLayerCache(renderContext);

  // 批量布局节点以减少重复计算
  for (let i = 0; i < nodeCount; i++) {
    layoutNode(renderContext, nodes[i]);
  }

  // 优化文本更新
  renderContext.providers.text.update();

  const topNodes = renderContext.api.getChildren(null);
  const scaleProvider = renderContext.providers.scale;
  const scale = scaleProvider.scale;

  // 缓存视口计算以避免重复
  const viewport = Rect.from({
    x: -scaleProvider.translateX / scale,
    y: -scaleProvider.translateY / scale,
    w: renderContext.context.canvas.width / scale,
    h: renderContext.context.canvas.height / scale,
  });
  renderContext.viewport = viewport;

  // 缓存视口检查函数
  renderContext.isNodeInViewport =
    renderContext.isNodeInViewport ||
    ((node) => {
      return isNodeInViewport(renderContext, node);
    });

  // 演示模式的快速路径
  if (renderContext.mode === 'presentation') {
    const currentFrameId = renderContext.currentFrameId;
    const topNodeCount = topNodes.length;
    for (let i = 0; i < topNodeCount; i++) {
      const node = topNodes[i];
      if (currentFrameId === node.id) {
        renderNode(renderContext, node);
        break; // 找到目标帧后立即退出
      }
    }
    return;
  }
  const { context } = renderContext;
  context.save();
  context.beginPath();
  context.rect(viewport.x, viewport.y, viewport.w, viewport.h);
  context.clip();

  // 普通模式的视口裁剪渲染
  const topNodeCount = topNodes.length;
  for (let i = 0; i < topNodeCount; i++) {
    const node = topNodes[i];
    if (renderContext.isNodeInViewport(node)) {
      renderNode(renderContext, node);
    }
  }

  context.restore();
}

function renderMarkdownCard(renderContext) {
  const context = renderContext.context;
  const card = renderContext.card;
  const cardProps = card.props;
  const cardW = cardProps.w;
  const cardH = cardProps.h;
  const cardSize = { w: cardW, h: cardH };
  const radius = cardProps.radius ?? 10;
  const bgPadding = cardProps.padding;
  const cardPadding = cardProps.padding;

  // 批量绘制背景
  drawBackground(renderContext, 'key', cardProps.background, cardSize);

  // 预计算矩形坐标
  const rectX = bgPadding;
  const rectY = bgPadding;
  const rectWidth = cardW - (bgPadding << 1); // 位移操作优化乘法
  const rectHeight = cardH - (bgPadding << 1);

  drawRoundedRect(context, rectX, rectY, rectWidth, rectHeight, radius, cardProps.card);

  // 优化图像渲染
  const markdownCache = renderContext.markdownCache;
  if (markdownCache?.img) {
    const padding2x = cardPadding << 1;
    context.drawImage(
      markdownCache.img,
      rectX + cardPadding,
      rectY + cardPadding,
      rectWidth - padding2x,
      rectHeight - padding2x
    );
  }

  renderGeneratedBy(renderContext);
}

function drawRoundedRect(context, x, y, width, height, radius, color) {
  context.beginPath();
  context.fillStyle = color;

  // 优化圆角绘制路径
  const rightX = x + width;
  const bottomY = y + height;

  context.moveTo(x + radius, y);
  context.arcTo(rightX, y, rightX, bottomY, radius);
  context.arcTo(rightX, bottomY, x, bottomY, radius);
  context.arcTo(x, bottomY, x, y, radius);
  context.arcTo(x, y, rightX, y, radius);
  context.closePath();
  context.fill();
}
